import { useEffect } from 'react';
import { Capacitor, registerPlugin } from '@capacitor/core';
import { addHealthDataLoadedListener, syncHealthToSupabase } from '@/lib/appleHealth';
import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '@/lib/env';
import { syncWatchAuth, syncWatchProfile } from '@/lib/straivWatch';

const getStraivHealthConnect = () => {
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    return registerPlugin<{
      setHealthSyncCredentials: (opts: {
        accessToken: string;
        userId: string;
        supabaseUrl?: string;
        supabaseAnonKey?: string;
      }) => Promise<void>;
    }>('StraivHealthConnect');
  }
  return null;
};

/**
 * Lightweight health-sync side-effects per protected route mount:
 *
 *   - Push fresh Supabase credentials into the native background-sync helper
 *     so the iOS background task can keep uploading samples even when the
 *     app is killed (this avoids the 401 storm we used to see).
 *   - Re-run a Supabase sync whenever the native HealthKit reader emits a
 *     data-loaded event (e.g. after a HealthKit query finishes).
 *
 * The actual cold-start / foreground sync is owned by App.tsx (see
 * `lastForegroundSyncRef` block) and is intentionally NOT duplicated here -
 * having two listeners would double the noise without the single-flight
 * mutex doing any extra work for it.
 */
const isOnboardingPath = () =>
  typeof window !== 'undefined' && window.location.pathname.includes('/onboarding');

export function useHealthSync(userId: string | null) {
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
    if (isOnboardingPath()) return;

    const storeCredentials = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token || !session.user.id) return;
      const plugin = getStraivHealthConnect();
      if (!plugin) return;
      try {
        await plugin.setHealthSyncCredentials({
          accessToken: session.access_token,
          userId: session.user.id,
          supabaseUrl: SUPABASE_URL,
          supabaseAnonKey: SUPABASE_ANON_KEY,
        });
        await syncWatchAuth(
          session.access_token,
          SUPABASE_URL,
          SUPABASE_ANON_KEY,
          session.refresh_token ?? undefined,
        );
        await syncWatchProfile(session.user.id);
      } catch {
        // Ignore - background sync will retry next time we get fresh creds.
      }
    };
    storeCredentials();

    const { data: authSub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        void storeCredentials();
      }
    });

    const removeDataLoadedListener = addHealthDataLoadedListener(async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) return;
      try {
        await syncHealthToSupabase(session.user.id, { forceSync: true, skipFeedback: true });
      } catch {
        // Silent
      }
    });

    return () => {
      authSub?.subscription.unsubscribe();
      removeDataLoadedListener?.();
    };
  }, [userId]);
}
